import React, { useEffect, useRef, useState, useMemo } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { LettersApi } from '@/api';
import type { LetterSseEvent } from '@/types/letters';
import { RiArrowLeftLine as BackIcon, RiCheckLine, RiLoader4Line } from 'react-icons/ri';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

// Generation stages with progress thresholds
const GENERATION_STAGES = [
  { id: 'init', label: 'Initializing', threshold: 0, description: 'Setting up letter generation...' },
  { id: 'docs', label: 'Processing Documents', threshold: 5, description: 'Reading and analyzing case documents...' },
  { id: 'search', label: 'Searching & Matching', threshold: 15, description: 'Finding relevant evidence and achievements...' },
  { id: 'analyze', label: 'Analyzing Evidence', threshold: 25, description: 'Extracting key qualifications and accomplishments...' },
  { id: 'generate', label: 'Writing Letter', threshold: 45, description: 'AI is composing your letter sections...' },
  { id: 'refine', label: 'Refining Content', threshold: 75, description: 'Polishing language and ensuring accuracy...' },
  { id: 'validate', label: 'Fact Checking', threshold: 90, description: 'Validating claims against source documents...' },
  { id: 'complete', label: 'Complete', threshold: 100, description: 'Letter generation finished!' },
];

interface LetterGenerationProgressProps {
  taskId: string;
  onBack: () => void;
  onFinal: (letterId?: string) => void;
}

const LetterGenerationProgress: React.FC<LetterGenerationProgressProps> = ({
  taskId,
  onBack,
  onFinal,
}) => {
  const [progress, setProgress] = useState<number>(0);
  const [, setStatus] = useState<string>('processing');
  const [messages, setMessages] = useState<Array<{ ts: number; text: string }>>(
    []
  );
  const [isFinal, setIsFinal] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<() => void>();
  const startTimeRef = useRef<number>(Date.now());

  // Calculate current stage based on progress
  const currentStage = useMemo(() => {
    for (let i = GENERATION_STAGES.length - 1; i >= 0; i--) {
      if (progress >= GENERATION_STAGES[i].threshold) {
        return GENERATION_STAGES[i];
      }
    }
    return GENERATION_STAGES[0];
  }, [progress]);

  useEffect(() => {
    // Subscribe to SSE
    unsubscribeRef.current = LettersApi.streamTask(
      taskId,
      (evt: LetterSseEvent) => {
        if (typeof evt.progress === 'number') setProgress(evt.progress);
        if (evt.status) {
          setStatus(evt.status);
          // Check for failed status
          if (evt.status === 'failed' || evt.status === 'error') {
            setError(evt.message || 'Letter generation failed');
            setIsFinal(true);

            analytics.track(ANALYTICS_EVENTS.LETTER_GENERATION_FAILED, {
              task_id: taskId,
              error_message: evt.message || 'Unknown error',
            });
          }
        }
        // Always append a readable message, even if 'message' field is absent
        const text =
          evt.message ||
          [
            evt.status ? `status: ${evt.status}` : null,
            typeof evt.progress === 'number'
              ? `progress: ${evt.progress}%`
              : null,
          ]
            .filter(Boolean)
            .join(' | ') ||
          'update';
        setMessages(prev => [
          ...prev,
          { ts: evt.timestamp || Date.now(), text },
        ]);
        if (evt.isFinal && evt.status !== 'failed' && evt.status !== 'error') {
          setIsFinal(true);

          // Track success with duration
          const duration = Date.now() - startTimeRef.current;
          analytics.track(ANALYTICS_EVENTS.LETTER_GENERATED, {
            task_id: taskId,
            letter_id: evt.letterId,
            duration_ms: duration,
            is_batch: false,
          });

          // Delay slightly to ensure backend writes are committed
          setTimeout(() => onFinal(evt.letterId), 400);
        }
      },
      () => {
        // On error, we keep the connection open; optionally we could show a toast.
      }
    );

    return () => {
      unsubscribeRef.current?.();
    };
  }, [taskId, onFinal]);

  // No activity log; we only show the latest status and percent

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="inline-flex items-center gap-1"
        >
          <BackIcon className="h-4 w-4" /> Back
        </Button>
        <h3 className="text-lg font-semibold text-neutral-900">
          Generating letter
        </h3>
      </div>

      <Card padding="lg" className="space-y-6">
        {error ? (
          <>
            <div className="flex items-center justify-center pt-12 pb-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center space-y-4">
              <h4 className="text-lg font-semibold text-red-900">
                Generation Failed
              </h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                <p className="text-sm text-red-800 font-mono whitespace-pre-wrap break-words">
                  {error}
                </p>
              </div>
              <div className="flex justify-center gap-3 pt-4">
                <Button variant="outline" onClick={onBack}>
                  <BackIcon className="h-4 w-4 mr-2" />
                  Back to Letters
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Progress bar */}
            <div className="w-full bg-neutral-200 rounded-full h-2 mb-6">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Stage indicators */}
            <div className="space-y-3 mb-6">
              {GENERATION_STAGES.slice(0, -1).map((stage, index) => {
                const isCompleted = progress > stage.threshold;
                const isCurrent = currentStage.id === stage.id;

                return (
                  <div
                    key={stage.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      isCurrent
                        ? 'bg-primary-50 border border-primary-200'
                        : isCompleted
                          ? 'bg-green-50'
                          : 'bg-neutral-50'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isCurrent
                          ? 'bg-primary-500 text-white'
                          : 'bg-neutral-300 text-neutral-500'
                    }`}>
                      {isCompleted ? (
                        <RiCheckLine className="w-4 h-4" />
                      ) : isCurrent ? (
                        <RiLoader4Line className="w-4 h-4 animate-spin" />
                      ) : (
                        <span className="text-xs font-medium">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        isCurrent ? 'text-primary-900' : isCompleted ? 'text-green-800' : 'text-neutral-500'
                      }`}>
                        {stage.label}
                      </p>
                      {isCurrent && (
                        <p className="text-xs text-primary-600 mt-0.5">
                          {messages[messages.length - 1]?.text || stage.description}
                        </p>
                      )}
                    </div>
                    {isCurrent && (
                      <span className="text-sm font-medium text-primary-600">
                        {progress}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Estimated time */}
            <div className="text-center">
              <p className="text-xs text-neutral-500">
                Estimated time: 2-3 minutes per letter
              </p>
            </div>

            {isFinal && !error && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                <p className="text-sm text-green-700 font-medium">
                  ✓ Generation finished. Opening the letter…
                </p>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
export default LetterGenerationProgress;
